import { useEffect, useMemo, useState } from "react";

import type { GeolocationLike } from "../capture/geolocation";
import { createLocalStorageQueueStore, queueCounts, type QueueStore } from "../capture/offline-queue";
import { useLocationStatus, type PermissionsLike } from "../capture/use-location-status";
import type { DeviceContext } from "../lib/device";
import { NotificationPreferences } from "../notifications/NotificationPreferences";
import { readBrowserNotificationFacts } from "../notifications/notification-readiness";
import { fieldStatusSections, readRuntimeFacts } from "./field-status";
import { PwaReadiness } from "./PwaReadiness";
import { SupportCard } from "./SupportCard";
import { detectQueueStorageAvailable, detectServiceWorkerActive } from "./pwa-readiness";

// Field Status — the read-only "why is this phone behaving like that?" screen. Everything here is
// a safe label or count; the facts→rows mapping lives in the pure model (field-status.ts). The only
// interactive row is location, which reuses the duty screen's enable-location flow.

export interface FieldStatusProps {
  appVersion: string;
  device: DeviceContext;
  checkpointCount: number;
  hasRouteAssignment: boolean;
  /** Resolved tenant/user method gates; defaults to all-on for callers without a flag verdict. */
  captureFlags?: { qrCapture: boolean; nfcCapture: boolean; manualCapture: boolean };
  /** Timeclock flag (web_timeclock); dark by default. */
  timeclockEnabled?: boolean;
  /** Browser notification flag (web_notifications); dark by default. */
  notificationsEnabled?: boolean;
  onBack: () => void;
  /** Test seams. */
  win?: Window;
  queueStore?: QueueStore;
  geolocation?: GeolocationLike | undefined;
  permissions?: PermissionsLike | null;
}

export function FieldStatus({
  appVersion,
  device,
  checkpointCount,
  hasRouteAssignment,
  captureFlags = { qrCapture: true, nfcCapture: true, manualCapture: true },
  timeclockEnabled = false,
  notificationsEnabled = false,
  onBack,
  win,
  queueStore,
  geolocation,
  permissions,
}: FieldStatusProps) {
  const w = win ?? window;
  const location = useLocationStatus({ geolocation, ...(permissions === undefined ? {} : { permissions }) });
  const [isOnline, setIsOnline] = useState(() => readRuntimeFacts(w).isOnline);
  const queueStorageAvailable = useMemo(() => {
    if (queueStore !== undefined) {
      return true;
    }
    try {
      return detectQueueStorageAvailable(w.localStorage);
    } catch {
      return false;
    }
  }, [queueStore, w]);
  const serviceWorkerActive = detectServiceWorkerActive(w.navigator);
  // Read-only support/permission facts; the one permission PROMPT lives behind the explicit
  // button inside NotificationPreferences, never here.
  const notificationFacts = useMemo(() => readBrowserNotificationFacts(w), [w]);

  useEffect(() => {
    if (typeof w.addEventListener !== "function") {
      return;
    }
    const onOnline = () => setIsOnline(true);
    const onOffline = () => setIsOnline(false);
    w.addEventListener("online", onOnline);
    w.addEventListener("offline", onOffline);
    return () => {
      w.removeEventListener("online", onOnline);
      w.removeEventListener("offline", onOffline);
    };
  }, [w]);

  // Queue counts are a snapshot of this phone's persisted queue, re-read when connectivity flips
  // (the moment the auto-replay drains it). The duty screen owns syncing; Status only reports.
  const counts = useMemo(() => {
    const store = queueStore ?? createLocalStorageQueueStore();
    return queueCounts(store.load());
  }, [queueStore, isOnline]);

  const sections = fieldStatusSections({
    ...readRuntimeFacts(w),
    isOnline,
    appVersion,
    device,
    location: { readiness: location.readiness, accuracyMeters: location.accuracyMeters },
    pendingQueueCount: counts.queued + counts.syncing,
    rejectedQueueCount: counts.rejected,
    checkpointCount,
    hasRouteAssignment,
    captureFlags,
    timeclockEnabled,
  });

  return (
    <section className="fieldstatus" aria-label="Field status">
      <div className="fieldstatus__head">
        <h2 className="fieldstatus__title">Field status</h2>
        <button className="fieldstatus__back" type="button" onClick={onBack}>
          Back to duty
        </button>
      </div>
      <PwaReadiness
        device={device}
        captureFlags={captureFlags}
        serviceWorkerActive={serviceWorkerActive}
        storageAvailable={queueStorageAvailable}
        notificationsEnabled={notificationsEnabled}
      />
      <NotificationPreferences
        flagEnabled={notificationsEnabled}
        apiSupported={notificationFacts.apiSupported}
        initialPermission={notificationFacts.permission}
        requestPermission={() =>
          (w as Window & { Notification: { requestPermission(): Promise<string> } }).Notification.requestPermission()
        }
      />
      {sections.map((section) => (
        <div className="fieldstatus__section" key={section.title}>
          <h3 className="fieldstatus__section-title">{section.title}</h3>
          <ul className="fieldstatus__rows">
            {section.rows.map((row) => (
              <li className="fieldstatus__row" key={row.key}>
                <div className="fieldstatus__rowtop">
                  <span className="fieldstatus__label">{row.label}</span>
                  {row.actionable === true ? (
                    <button
                      className={`status-chip status-chip--${row.tone}`}
                      type="button"
                      onClick={() => void location.requestLocation()}
                    >
                      {row.value}
                    </button>
                  ) : (
                    <span className={`status-chip status-chip--${row.tone}`}>{row.value}</span>
                  )}
                </div>
                {row.detail !== undefined ? <p className="fieldstatus__detail">{row.detail}</p> : null}
              </li>
            ))}
          </ul>
        </div>
      ))}
      <SupportCard appVersion={appVersion} />
    </section>
  );
}
